import type { Recipe, RecipeStore } from '@platewell/core';
import type { RecipeSearchIndex } from './search.js';
import { browse, type BrowseQuery } from './selectors.js';

/**
 * The list a recipe was opened from.
 *
 * Both clients carry this in the URL rather than in memory, so paging through results and
 * returning to the list survive a reload, a share link, and the back button. Keeping it as a
 * discriminated union means the precedence between the raw params is decided exactly once,
 * in {@link parseListContext}, instead of at every call site.
 */
export type ListContext =
  | { readonly kind: 'collection'; readonly collectionId: string }
  | { readonly kind: 'favourites' }
  | { readonly kind: 'recent' }
  | { readonly kind: 'tag'; readonly tag: string; readonly term: string }
  | { readonly kind: 'search'; readonly term: string }
  | { readonly kind: 'all' };

/** Lists that have no filter of their own, and so need a name to be linked back to. */
export type ListSource = 'all' | 'favourites' | 'recent';

/** How many recipes the Recently Added list shows. */
export const RECENT_RECIPES_LIMIT = 20;

/** Raw route params, as loosely typed as each router hands them over. */
export interface ListContextParams {
  readonly q?: string | null;
  readonly tag?: string | null;
  readonly collectionId?: string | null;
  readonly from?: string | null;
}

function text(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Resolves raw params into a context, or null when the recipe was not opened from a list.
 *
 * Precedence is collection, then favourites, then recent, then tag, then search, then All
 * Recipes. A tag keeps any search term alongside it because the tag list applies both
 * together; favourites and recent drop one because neither list has a search box.
 */
export function parseListContext(params: ListContextParams): ListContext | null {
  const collectionId = text(params.collectionId);
  if (collectionId !== '') return { kind: 'collection', collectionId };

  const from =
    params.from === 'all' || params.from === 'favourites' || params.from === 'recent'
      ? params.from
      : undefined;
  if (from === 'favourites') return { kind: 'favourites' };
  if (from === 'recent') return { kind: 'recent' };

  const term = text(params.q);
  const tag = text(params.tag);
  if (tag !== '') return { kind: 'tag', tag, term };
  if (term !== '') return { kind: 'search', term };
  if (from === 'all') return { kind: 'all' };
  return null;
}

/**
 * The one definition of the ordered list a context resolves to.
 *
 * Every list view and both recipe detail screens go through this, so the pager can never fall
 * out of step with the list it is paging through the way it did before commit 5c5261a.
 */
export function listContextQuery(context: ListContext): BrowseQuery {
  switch (context.kind) {
    case 'collection':
      return { collectionId: context.collectionId, sort: 'title' };
    case 'favourites':
      return { onlyFavourites: true, sort: 'title' };
    case 'recent':
      // Trashed recipes are filtered out before the limit is applied, so this is always the
      // newest twenty live recipes rather than twenty minus however many are in the trash.
      return { sort: 'createdAt', limit: RECENT_RECIPES_LIMIT };
    case 'tag':
      return {
        term: context.term,
        tags: [context.tag],
        sort: context.term === '' ? 'title' : undefined,
      };
    case 'search':
      return { term: context.term };
    case 'all':
      return { sort: 'title' };
  }
}

/**
 * A context's list narrowed by a search term, in relevance order, for pickers that search
 * inside a list.
 *
 * Merging the term into {@link listContextQuery} would not do for Recently Added: a term
 * lifts the limit, so it would search every recipe rather than the newest twenty. Searching
 * everything and keeping only the list's members holds for every kind of list.
 *
 * Reading the list is as costly as the search itself, since both read every recipe out of
 * the document. Callers that search as the user types pass `listed` in, read once per list,
 * so each keystroke pays for the search alone.
 */
export function searchListContext(
  store: RecipeStore,
  index: RecipeSearchIndex | null,
  context: ListContext,
  term: string,
  listed?: Recipe[],
): Recipe[] {
  const hits = (): Recipe[] => browse(store, index, { term, limit: Number.MAX_SAFE_INTEGER });
  // All Recipes is every live recipe, which is what a search already covers.
  if (term.trim() !== '' && context.kind === 'all') return hits();

  const members = listed ?? browse(store, index, listContextQuery(context));
  if (term.trim() === '') return members;
  const ids = new Set(members.map((recipe) => recipe.id));
  return hits().filter((recipe) => ids.has(recipe.id));
}

/** False for All Recipes, which per features/recipe-navigation.md never shows prev/next. */
export function listContextPages(context: ListContext): boolean {
  return context.kind !== 'all';
}

/** Turns a context back into link params, so a recipe link carries the list it came from. */
export function listContextParams(context: ListContext): {
  q?: string;
  tag?: string;
  collectionId?: string;
  from?: ListSource;
} {
  switch (context.kind) {
    case 'collection':
      return { collectionId: context.collectionId };
    case 'favourites':
      return { from: 'favourites' };
    case 'recent':
      return { from: 'recent' };
    case 'tag':
      return context.term === '' ? { tag: context.tag } : { tag: context.tag, q: context.term };
    case 'search':
      return { q: context.term };
    case 'all':
      return { from: 'all' };
  }
}
